import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, BookOpen } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      const res = await axios.get(`${API}/articles/${slug}`);
      setArticle(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching article:', err);
      setError(true);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-background">
        <p className="text-xl text-muted-foreground">Duke ngarkuar artikullin...</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-background px-4">
        <BookOpen className="w-16 h-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold text-foreground mb-2">Artikulli nuk u gjet</h2>
        <p className="text-muted-foreground mb-6 text-center max-w-md">
          Artikulli që po kërkoni mund të jetë fshirë ose adresa nuk është e saktë.
        </p>
        <Link to="/" className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition">
          Kthehu në Ballinë
        </Link>
      </div>
    );
  }

  return (
    <motion.article 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-background py-24"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-primary hover:text-primary-700 font-medium mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Kthehu
        </Link>
        
        <header className="mb-10">
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground mb-6 leading-tight">
            {article.title}
          </h1>
          <div className="flex items-center gap-2 text-muted-foreground bg-accent/10 w-fit px-4 py-2 rounded-full">
            <Calendar className="w-4 h-4 text-accent" />
            <span className="font-medium text-sm">
              {new Date(article.created_at).toLocaleDateString('sq-AL', { 
                year: 'numeric', month: 'long', day: 'numeric' 
              })}
            </span>
          </div>
        </header>

        <div 
          className="prose prose-lg md:prose-xl max-w-none text-foreground/80 leading-relaxed
            prose-headings:font-heading prose-headings:text-foreground
            prose-a:text-primary hover:prose-a:text-primary-700 transition-colors
            prose-strong:text-foreground
            prose-blockquote:border-accent prose-blockquote:bg-accent/5 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg"
          dangerouslySetInnerHTML={{ __html: article.content }} 
        />
        
        <div className="mt-16 pt-8 border-t flex justify-center">
          <Link to="/" className="px-8 py-3 bg-gradient-to-r from-primary to-primary-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all">
            Kthehu në Ballinë
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

export default ArticlePage;
